import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import ContactNote from 'types/ContactNote';
import { GlobalState } from 'reducers/stateTypes';
import { closeNoteDrawer, openNoteDrawer, setNoteDisplayDialog, setUsedInBr } from 'reducers/contactNoteListReducer';
import ContactNoteFormContainer from 'views/ContactNoteForm/ContactNoteFormContainer';
import ContactNoteDetails from 'views/ContactNoteDetails';
import ContactNotes from 'views/ContactNotes/ContactNotes';
import { isNotNullOrUndefined } from 'utils/utils';
import { EnvConfig } from 'config/envConfig';
import { ZfStack } from '@zafire/react';
import NoteDetails from 'views/ContactNoteDetails/zafire/NoteDetails';
import { JBSplitLayout } from '@juliusbaer-gcrm/gcrm-frontend-components';
import RightDrawer from 'components/Common/RightDrawer';
import ContactNoteSelect from 'views/ContactNoteSelect';

/**
 * Enum options for the possible components that the RightDrawer should display
 */
export enum DisplayDialogs {
  EDIT_VIEW = 'editNote',
  NEW_NOTE_VIEW = 'newNote',
  DEFAULT_NOTE_VIEW = 'default',
}

/**
 * A container component to display the ContactNotes and the ContactNoteSelect components,
 * as well as the openable RightDrawer.
 */
const ContactNotesContainer = ({ usedInBr = false }: { usedInBr?: boolean }) => {
  const dispatch = useDispatch();
  const displayDialog: string = useSelector((state: GlobalState) => state.contactNoteList.displayDialog);
  const shownNote: ContactNote | null = useSelector((state: GlobalState) => state.contactNoteList.shownNote);
  const noteDrawerOpen: boolean = useSelector((state: GlobalState) => state.contactNoteList.drawerOpen);
  const { create } = useParams<{ create: string }>();

  useEffect(() => {
    dispatch(setUsedInBr(usedInBr));
  }, [usedInBr]);

  useEffect(() => {
    if (isNotNullOrUndefined(create)) {
      dispatch(setNoteDisplayDialog(DisplayDialogs.NEW_NOTE_VIEW));
      if (!EnvConfig.config.createContactNoteNewUiEnabled) {
        dispatch(openNoteDrawer());
      }
    }
  }, [create]);

  /**
   * Function to select which component the RightDrawer displays.
   */
  const componentToRender = (shownNote: ContactNote | null) => {
    switch (displayDialog) {
      case DisplayDialogs.EDIT_VIEW: {
        if (EnvConfig.config.createContactNoteNewUiEnabled) {
          return null;
        }
        return (
          <ContactNoteFormContainer
            edit={true}
            contactNote={shownNote}
            cancelAction={() => dispatch(setNoteDisplayDialog(DisplayDialogs.DEFAULT_NOTE_VIEW))}
          />
        );
      }

      case DisplayDialogs.NEW_NOTE_VIEW: {
        if (EnvConfig.config.createContactNoteNewUiEnabled) {
          return null;
        }
        return (
          <ContactNoteFormContainer cancelAction={() => dispatch(closeNoteDrawer())} contactNote={null} edit={false} />
        );
      }

      case DisplayDialogs.DEFAULT_NOTE_VIEW: {
        return <ContactNoteDetails />;
      }
    }
  };

  return (
    <>
      {!EnvConfig.config.createContactNoteNewUiEnabled && (
        <JBSplitLayout
          leftSideContent={<ContactNotes usedInBr={usedInBr} />}
          rightSideContent={
            <>
              <RightDrawer>{componentToRender(shownNote)}</RightDrawer>
              {!EnvConfig.config.createContactNoteNewUiEnabled && <ContactNoteSelect />}
            </>
          }
        />
      )}
      {EnvConfig.config.createContactNoteNewUiEnabled && (
        <ZfStack direction="row" style={{ height: '100vh', overflow: 'hidden' }}>
          <ZfStack
            direction="column"
            style={{ flex: 1, overflow: 'auto', maxWidth: noteDrawerOpen ? '50vw' : 'unset' }}
          >
            <ContactNotes usedInBr={usedInBr} />
          </ZfStack>
          <NoteDetails />
        </ZfStack>
      )}
    </>
  );
};

export default ContactNotesContainer;
